import * as XLSX from 'xlsx';
import { QuestionTypeEnum } from '@/types/composition-functions';
import { SAMPLE_IMAGE_PNG_BASE64 } from '@/utils/cbtTemplateAssets';

// No 'csv': the backend only parses .xlsx/.xls as a sheet; any other file is read as
// Aiken text, so a CSV copy of the sheet template could never be imported.
export type TemplateFormat = 'txt' | 'xlsx' | 'docx'

export const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const XLSX_HEADER = [
  'QUESTION_ID', 'QUESTION_TEXT', 'QUESTION_IMAGE_1', 'QUESTION_IMAGE_2',
  'OPTION_LABEL', 'OPTION_TEXT', 'OPTION_MEDIA', 'IS_CORRECT',
];

const TXT_TEMPLATES: Partial<Record<QuestionTypeEnum, string>> = {
  [QuestionTypeEnum.TrueFalse]: [
    'The sun rises in the east.',
    'ANSWER: TRUE',
    '',
    'Water boils at 50 degrees Celsius.',
    'ANSWER: FALSE',
    '',
    'The human heart has four chambers.',
    'ANSWER: TRUE',
    '',
    'All mammals can fly.',
    'ANSWER: FALSE',
    '',
    'Python is a programming language.',
    'ANSWER: TRUE',
    '',
  ].join('\n'),
  [QuestionTypeEnum.FillInTheBlanks]: [
    'The capital city of Nigeria is [blank].',
    'ANSWER: Abuja',
    '',
    'The chemical symbol for water is _____.',
    'ANSWER: H2O',
    '',
    'The process by which plants make their own food is called [blank].',
    'ANSWER: Photosynthesis',
    '',
    'The currency used in Japan is [blank].',
    'ANSWER: Yen',
    '',
    'A year has _____ months.',
    'ANSWER: 12',
    '',
    'The largest planet in our solar system is [blank].',
    'ANSWER: Jupiter',
    '',
    'The fastest land animal is the [blank].',
    'ANSWER: Cheetah',
    '',
    'Light [blank] travels at approximately [blank] kilometers per second.',
    'ANSWER: 300,000',
    'ANS: 400,000',
    '',
    'The human body has _____ bones.',
    'ANSWER: 206',
    '',
    'Water freezes at _____ degrees Celsius.',
    'ANSWER: 0',
    '',
  ].join('\n'),
  [QuestionTypeEnum.SingleChoice]: [
    'According to Genesis 1:1, who is credited with the creation of the heavens and the earth',
    'A. Angels',
    'B. God',
    'C. Man',
    'D. Nature',
    'ANS: B',
    '',
    'What did Christ take from Satan after His victory over him?',
    'A. Thrones',
    'B. Keys of Hell and Death',
    'C. Precious stones',
    'D. Angelic armies',
    'ANSWER: B',
    '',
    'According to Revelation 4:11, for what purpose were the heavens and the earth created?',
    'A. Man\'s use',
    'B. Angels\' habitation',
    'C. God\'s pleasure',
    'D. Satan\'s dominion',
    'ANSWER: C',
    '',
    'Which archangel is known as the captain of the heavenly guard?',
    'A. Gabriel',
    'B. Lucifer',
    'C. Michael',
    'D. Raphael',
    'ANSWER: C',
    '',
    'What was the primary cause of Lucifer\'s downfall?',
    'A. Weakness',
    'B. Ignorance',
    'C. Fear',
    'D. Pride',
    'ANSWER: D',
    '',
  ].join('\n'),
  [QuestionTypeEnum.MultipleChoice]: [
    'Which of the following are primary colors? (Select all that apply)',
    'A. Red',
    'B. Green',
    'C. Blue',
    'D. Yellow',
    'ANSWER: A,C,D',
    '',
    'Which of these are programming languages?',
    'A. Python',
    'B. HTML',
    'C. JavaScript',
    'D. Microsoft Word',
    'ANSWER: A,C',
    '',
    'Which planets in our solar system are gas giants?',
    'A. Mercury',
    'B. Jupiter',
    'C. Earth',
    'D. Saturn',
    'ANSWER: B,D',
    '',
    'Which of the following are even numbers?',
    'A. 3',
    'B. 8',
    'C. 11',
    'D. 14',
    'ANSWER: B,D',
    '',
  ].join('\n'),
};

const SHEET_TEMPLATES: Partial<Record<QuestionTypeEnum, (string | number)[][]>> = {
  [QuestionTypeEnum.SingleChoice]: [
    [1, 'Identify the image', 'https://example.com/question1_img1.png', 'https://example.com/question1_img2.png', 'A', 'War', 'https://example.com/optionA.png', 1],
    [1, 'Identify the image', '', '', 'B', 'House', 'https://example.com/optionB.png', 0],
    [1, 'Identify the image', '', '', 'C', 'Shop', '', 0],
    [1, 'Identify the image', '', '', 'D', 'Road', '', 0],
    [2, 'Who is this', '', '', 'A', 'Aintola', '', 0],
    [2, 'Who is this', '', '', 'B', 'Alelabu', '', 0],
    [2, 'Who is this', '', '', 'C', 'Awolowo', '', 0],
    [2, 'Who is this', '', '', 'D', 'Obasanjo', '', 1],
  ],
  [QuestionTypeEnum.MultipleChoice]: [
    [1, 'Which of these are primary colors?', '', '', 'A', 'Red', '', 1],
    [1, 'Which of these are primary colors?', '', '', 'B', 'Green', '', 0],
    [1, 'Which of these are primary colors?', '', '', 'C', 'Blue', '', 1],
    [1, 'Which of these are primary colors?', '', '', 'D', 'Yellow', '', 1],
    [2, 'Which of these are programming languages?', '', '', 'A', 'Python', '', 1],
    [2, 'Which of these are programming languages?', '', '', 'B', 'HTML', '', 0],
    [2, 'Which of these are programming languages?', '', '', 'C', 'JavaScript', '', 1],
    [2, 'Which of these are programming languages?', '', '', 'D', 'Microsoft Word', '', 0],
  ],
};

// Worked examples appended to each .docx template showing how to attach a
// picture. The image sits on the SAME line as the question/option it belongs
// to — which is exactly how the backend maps embedded pictures on import.
type DemoLine = { text?: string; image?: boolean };

const DOCX_IMAGE_DEMOS: Partial<Record<QuestionTypeEnum, DemoLine[]>> = {
  [QuestionTypeEnum.SingleChoice]: [
    { text: 'Identify the shape shown here: ', image: true },
    { text: 'A. Circle ', image: true },
    { text: 'B. Square' },
    { text: 'C. Triangle' },
    { text: 'ANSWER: A' },
  ],
  [QuestionTypeEnum.MultipleChoice]: [
    { text: 'Study the diagram below, then select all correct statements. ', image: true },
    { text: 'A. It has straight edges' },
    { text: 'B. It is a circle' },
    { text: 'C. It is a polygon' },
    { text: 'ANSWER: A,C' },
  ],
  [QuestionTypeEnum.TrueFalse]: [
    { text: 'The picture below shows a triangle. ', image: true },
    { text: 'ANSWER: FALSE' },
  ],
  [QuestionTypeEnum.FillInTheBlanks]: [
    { text: 'The animal shown in the picture is a [blank]. ', image: true },
    { text: 'ANSWER: Lion' },
  ],
};

export function availableFormats(type: QuestionTypeEnum | '' | null | undefined): TemplateFormat[] {
  if (!type) return [];
  const formats: TemplateFormat[] = [];
  if (TXT_TEMPLATES[type]) formats.push('txt', 'docx');
  if (SHEET_TEMPLATES[type]) { formats.push('xlsx'); }
  return formats;
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Build a Word (.docx) template for a question type. It mirrors the plain-text
 * (.txt) template content and appends one worked example with an embedded
 * picture so users can see how images are attached on import. The `docx`
 * library is loaded on demand to keep it out of the main bundle.
 */
export async function buildDocxBlob(type: QuestionTypeEnum): Promise<Blob | null> {
  const text = TXT_TEMPLATES[type];
  if (!text) return null;

  const {
    Document, Packer, Paragraph, TextRun, ImageRun,
  } = await import('docx');

  const paragraphs = text.split('\n').map(
    (line) => new Paragraph({ children: line ? [new TextRun(line)] : [] }),
  );

  const demo = DOCX_IMAGE_DEMOS[type];
  if (demo) {
    const imageBytes = base64ToUint8Array(SAMPLE_IMAGE_PNG_BASE64);
    paragraphs.push(new Paragraph({ children: [] })); // blank line separates blocks
    demo.forEach((line) => {
      paragraphs.push(new Paragraph({
        children: [
          ...(line.text ? [new TextRun(line.text)] : []),
          ...(line.image
            ? [new ImageRun({ type: 'png', data: imageBytes, transformation: { width: 150, height: 89 } })]
            : []),
        ],
      }));
    });
  }

  const doc = new Document({ sections: [{ children: paragraphs }] });
  return Packer.toBlob(doc);
}

export function rowsToCsv(rows: (string | number)[][]): string {
  return rows
    .map((row) => row.map((cell) => {
      const s = String(cell ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(','))
    .join('\r\n');
}

export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function downloadQuestionTemplate(type: QuestionTypeEnum, format: TemplateFormat): Promise<void> {
  const baseName = `${type.toLowerCase()}_template`;

  if (format === 'txt') {
    const content = TXT_TEMPLATES[type];
    if (!content) return;
    triggerDownload(new Blob([content], { type: 'text/plain;charset=utf-8' }), `${baseName}.txt`);
    return;
  }

  if (format === 'docx') {
    const blob = await buildDocxBlob(type);
    if (!blob) return;
    triggerDownload(blob, `${baseName}.docx`);
    return;
  }

  const body = SHEET_TEMPLATES[type];
  if (!body) return;
  const rows = [XLSX_HEADER, ...body];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, ws, 'Questions');
  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
  triggerDownload(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${baseName}.xlsx`,
  );
}
